// P9.2A §16 — Generation-lineage panel: the Builder explains WHERE a strategy came from
// and WHY each block exists, for both profiles:
//
//   • structural_setup_v1 saves (detected from compiler markers) expand into the full
//     lineage: profile id+version, proposition, stages, temporal ordering, state
//     transitions, timeframes, data dependencies, regions, confirmations, invalidation,
//     expiry, target thesis, block counts, evidence + commercial-target state, and a
//     per-block "why" table — a visually complex graph is never shown unexplained.
//   • every other save displays exactly "generated by legacy_block_first_v1" — historical
//     lineage is preserved, never migrated or relabeled.
//
// Purely presentational; derives everything from the block graph via lineageDisplay.ts.

import { useMemo, useState } from 'react';
import type { Block } from '../../types';
import { lineageViewFromSavedBlocks } from '../../engine/generation/lineageDisplay';

function Row({ label, values }: { label: string; values: string[] | string | null }) {
  if (!values || (Array.isArray(values) && values.length === 0)) return null;
  const list = Array.isArray(values) ? values : [values];
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
      {list.map((v, i) => <span key={i} className="text-xs text-slate-800">{v}</span>)}
    </div>
  );
}

export default function GenerationLineagePanel({ blocks }: { blocks: Block[] }) {
  const [open, setOpen] = useState(false);
  const view = useMemo(() => (blocks.length ? lineageViewFromSavedBlocks(blocks) : null), [blocks]);
  if (!view) return null;

  const legacy = view.profileId === 'legacy_block_first_v1';
  return (
    <div
      role="note"
      aria-label="Strategy generation lineage"
      data-testid="generation-lineage-panel"
      className={`px-3 py-2 mb-2 text-xs rounded-md border ${legacy ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold" data-testid="lineage-profile">{view.profileLabel}</span>
        {view.setupSlug && <span className="font-mono">{view.setupSlug}</span>}
        {view.proposition && <span className="truncate max-w-[40ch]" title={view.proposition}>{view.proposition}</span>}
        <span>{view.rawBlockCount} blocks{view.semanticBlockCount != null ? ` (${view.semanticBlockCount} semantic)` : ''}</span>
        <span className="text-[11px] opacity-80">{view.evidenceState}</span>
        {!legacy && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="ml-auto underline focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded"
            data-testid="lineage-toggle"
          >
            {open ? 'Hide lineage' : 'Explain this strategy →'}
          </button>
        )}
      </div>
      {open && !legacy && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lineage-detail">
          <Row label="Setup IR" values={view.setupIrId} />
          <Row label="Stages" values={view.stages?.join(' → ') ?? null} />
          <Row label="Timeframes" values={view.timeframes} />
          <Row label="Temporal ordering" values={view.temporalOrdering} />
          <Row label="State transitions" values={view.stateTransitions} />
          <Row label="Data dependencies" values={view.dataDependencies} />
          <Row label="References & regions" values={view.referencesAndRegions} />
          <Row label="Confirmations" values={view.confirmations} />
          <Row label="Invalidation" values={view.invalidation} />
          <Row label="Expiry" values={view.expiry} />
          <Row label="Target thesis" values={view.targetThesis} />
          <Row label="Commercial target" values={view.commercialTargetState} />
          {view.unsupportedCapabilities.length > 0 && <Row label="Unsupported capabilities" values={view.unsupportedCapabilities} />}
          <div className="sm:col-span-2 lg:col-span-3">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">Why each block exists</span>
            <div className="mt-1 max-h-48 overflow-y-auto border border-emerald-100 rounded">
              <table className="w-full text-[11px]">
                <tbody>
                  {view.blockExplanations.map((e) => (
                    <tr key={e.blockId} className="border-b border-emerald-50 align-top">
                      <td className="px-2 py-1 font-mono whitespace-nowrap">{e.name}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-emerald-700">{e.stage}</td>
                      <td className="px-2 py-1">{e.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
